import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';
import { CompetitionService } from '../../service/CompetitionService';
import { translatedMessage } from '../../service/LanguageService';
import { Dropdown } from 'primereact/dropdown';
import QuestionListComponent from './QuestionListComponent';

let emptySubjectProposal = {
    id: null,
    subject: {
        id: null
    },
    questions: [],
    author: {
        id: null
    }
};

const SubjectProposalEditDialog = (props) => {
    const [proposal, setProposal] = useState(emptySubjectProposal);
    const [isNew, setIsNew] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);

    const toast = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);

    useEffect(() => {
        let _proposal = props.value ? props.value : emptySubjectProposal;
        setProposal(_proposal);
        setIsNew(!props.value?.id);
    }, [props.value, props.visible]);

    useEffect(() => {
        setDialogVisible(props.visible);
    }, [props.visible]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: proposal,
        validate: (data) => {
            let errors = {};

            if (!data.subject || !data.subject.id) {
                errors.subject = translatedMessage('form.error.subject.required');
            }
            return errors;
        },
        onSubmit: (data) => {
            saveSubjectProposal(data);
            formik.resetForm();
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const hideSubjectProposalDialog = () => {
        if (typeof props.visibleSetter === 'function') {
            props.visibleSetter(false);
        }
        setDialogVisible(false);
        formik.resetForm();
        refreshParent();
    };

    const refreshParent = () => {
        if (typeof props.afterSave === 'function') {
            props.afterSave(proposal, false);
        }
    };

    const saveSubjectProposal = (data) => {
        data.subject = { id: data.subject.id };
        competitionService
            .saveSubjectProposal(data)
            .then((savedSubjectProposal) => {
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
                setProposal(savedSubjectProposal);
                if (!isNew) {
                    hideSubjectProposalDialog();
                }
                setIsNew(!savedSubjectProposal?.id);
                refreshParent();
            })
            .catch((error) => {
                if (Array.isArray(error)) {
                    const delay = 100;
                    error.forEach((singleError, index) => {
                        const cumulativeDelay = index * delay;
                        setTimeout(() => {
                            toast.current.show({ severity: 'error', summary: singleError, life: 5000 });
                        }, cumulativeDelay);
                    });
                } else {
                    toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
                }
            });
    };

    const afterSaveQuestion = (savedQuestion, isNew) => {
        if (typeof props.afterSave === 'function') {
            props.afterSave(proposal, false);
        }
    };

    const subjectDialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideSubjectProposalDialog} />
            <Button label={translatedMessage('generic.save')} icon="pi pi-save" className="p-button-text" form="subjectProposal-form" type="submit" />
        </>
    );

    const setSubject = (value) => {
        let _subject = props.subjects.find((s) => s.id === value);
        formik.setFieldValue('subject', _subject);
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog visible={dialogVisible} header={translatedMessage('competition.subjectProposal.details')} modal className="pcn-dialog" footer={subjectDialogFooter} onHide={hideSubjectProposalDialog}>
                <form id="subjectProposal-form" onSubmit={formik.handleSubmit}>
                    <div className="field p-inputgroup mt-2 mb-1">
                        <span className="p-float-label">
                            <Dropdown id="subject" name="subject" value={formik.values.subject.id} options={props.subjects} optionLabel="name" optionValue="id" onChange={(e) => setSubject(e.value)} />

                            <label htmlFor="subject" className={classNames({ 'p-error': isFormFieldValid('subject') })}>
                                {translatedMessage('competition.subject')}
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('subject')}

                    {proposal.id && (
                        <>
                            <QuestionListComponent
                                questions={proposal.questions}
                                proposal={proposal}
                                canEdit={props.canEdit}
                                afterSave={afterSaveQuestion}
                                questionTypes={props.questionTypes}
                                competition={props.competition}
                                permissions={props.permissions}
                            ></QuestionListComponent>
                        </>
                    )}
                </form>
            </Dialog>
        </>
    );
};

export default SubjectProposalEditDialog;
